import { useMutation } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { useSnackbar } from "../contexts/SnackbarContext";
import { backendService } from "../services/backend.service";
import { hasHrisIntegrationOfType } from "../utils/hrisIntegrations";

type SyncShiftsParams = { file?: File; startDate?: DateTime; endDate?: DateTime };

export const useSyncShifts = () => {
	const { showErrorSnackbar, chosenDepartment } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { track } = useAnalytics();
	const hasShiftsIntegration = hasHrisIntegrationOfType(chosenDepartment, "shifts");

	return useMutation({
		mutationFn: async (params: SyncShiftsParams) => {
			if (!hasShiftsIntegration) return;
			const response = await backendService.syncShifts({ ...params, departmentId: chosenDepartment.department_id });
			return response.data;
		},
		onSuccess: (data) => {
			if (!data) return;

			const insertCount = data.toInsert.length;
			const deleteCount = data.toDelete.length;
			const ignoreCount = data.toIgnore.length;

			let message = `Sync completed: ${insertCount} inserted, ${deleteCount} deleted`;
			if (ignoreCount) message += `, ${ignoreCount} ignored`;

			showSnackbar(message, "success");

			track("Integrations - Shifts synced", {
				department_id: chosenDepartment.department_id,
				inserted: insertCount,
				deleted: deleteCount,
				ignored: ignoreCount,
			});
		},
		onError: (error) => {
			showErrorSnackbar(
				"Failed to sync shifts",
				{
					action: "sync shifts failed",
					department_id: chosenDepartment.department_id,
				},
				error,
			);
		},
	});
};
